import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL, RECAPTCHA_SITE_KEY, isLocalhost } from './config';

// Get reCAPTCHA site key from config
// const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LfCr-MqAAAAAEMsObYjY1fVi0Wn0A5MpyOA5AN9';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null },
  });
  
  // Add a ref to track if the component is mounted
  const isMounted = useRef(true);
  // Add a ref to track reCAPTCHA script loading status
  const recaptchaLoaded = useRef(false);
  // Add a ref to track if reCAPTCHA is ready
  const recaptchaReady = useRef(false);
  // Add a ref to track retry attempts
  const retryAttempts = useRef(0);
  const MAX_RETRY_ATTEMPTS = 5;
  
  // Check if we're on localhost - now imported from config.js
  // const isLocalhost = window.location.hostname === 'localhost' || 
  //                    window.location.hostname === '127.0.0.1';

  // Load the reCAPTCHA v3 script
  useEffect(() => {
    console.log('Component mounted');
    console.log('Running on localhost:', isLocalhost);
    
    // Set up cleanup function
    return () => {
      console.log('Component unmounting');
      isMounted.current = false;
    };
  }, [isLocalhost]);

  // Load reCAPTCHA script
  useEffect(() => {
    // Skip reCAPTCHA loading on localhost if needed
    if (isLocalhost) {
      console.log('Localhost detected - setting reCAPTCHA as ready for testing');
      recaptchaReady.current = true;
      return;
    }
    
    const loadRecaptchaScript = () => {
      // Check if reCAPTCHA is already available from the script in index.html
      if (window.grecaptcha && window.grecaptcha.ready) {
        console.log('reCAPTCHA already available from index.html script');
        window.grecaptcha.ready(() => {
          console.log('reCAPTCHA is ready (from existing script)');
          recaptchaReady.current = true;
          recaptchaLoaded.current = true;
        });
        return;
      }
      
      // Add reCAPTCHA script if it doesn't exist
      if (!window.grecaptcha && !recaptchaLoaded.current) {
        console.log('Loading reCAPTCHA script...');
        console.log('Using reCAPTCHA site key:', RECAPTCHA_SITE_KEY);
        recaptchaLoaded.current = true;
        
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
          console.log('reCAPTCHA script loaded successfully');
          // Initialize reCAPTCHA after script loads
          if (window.grecaptcha) {
            window.grecaptcha.ready(() => {
              console.log('reCAPTCHA is ready');
              recaptchaReady.current = true;
            });
          }
        };
        
        script.onerror = (error) => {
          console.error('Error loading reCAPTCHA script:', error);
          recaptchaLoaded.current = false;
        };
        
        document.head.appendChild(script);
        
        return () => {
          // Only remove if it exists
          if (document.head.contains(script)) {
            document.head.removeChild(script);
          }
        };
      } else if (window.grecaptcha && !recaptchaReady.current) {
        // If script is already loaded but not ready yet
        window.grecaptcha.ready(() => {
          console.log('reCAPTCHA is ready (from existing script)');
          recaptchaReady.current = true;
        });
      }
    };
    
    loadRecaptchaScript();
    
    // Check periodically if reCAPTCHA is ready
    const checkInterval = setInterval(() => {
      if (window.grecaptcha && !recaptchaReady.current) {
        window.grecaptcha.ready(() => {
          console.log('reCAPTCHA is ready (from interval check)');
          recaptchaReady.current = true;
        });
      }
      
      if (recaptchaReady.current || !isMounted.current) {
        clearInterval(checkInterval);
      }
    }, 1000);
    
    return () => {
      clearInterval(checkInterval);
    };
  }, [isLocalhost]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Use useCallback to memoize the function
  const executeRecaptcha = useCallback(async () => {
    console.log('Executing reCAPTCHA...');
    
    // For localhost testing, return a mock token
    if (isLocalhost) {
      console.log('Localhost detected - using mock reCAPTCHA token');
      return 'localhost-mock-recaptcha-token';
    }
    
    // Reset retry attempts if this is a new execution
    retryAttempts.current = 0;
    
    return new Promise((resolve, reject) => {
      const attemptRecaptcha = () => {
        // Check if component is still mounted
        if (!isMounted.current) {
          console.error('Component is unmounted, cannot execute reCAPTCHA');
          return reject(new Error('Component unmounted'));
        }
        
        // Check retry limit
        if (retryAttempts.current >= MAX_RETRY_ATTEMPTS) {
          console.error(`Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) exceeded`);
          return reject(new Error(`Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) exceeded`));
        }
        
        // Increment retry counter
        retryAttempts.current++;
        
        try {
          // Check if grecaptcha is available and ready
          if (!window.grecaptcha || !window.grecaptcha.execute) {
            console.log(`grecaptcha not loaded yet, retrying... (${retryAttempts.current}/${MAX_RETRY_ATTEMPTS})`);
            // If grecaptcha isn't loaded yet, wait a bit and try again
            setTimeout(attemptRecaptcha, 1000);
            return;
          }
          
          // Execute reCAPTCHA
          console.log('grecaptcha ready, executing with site key:', RECAPTCHA_SITE_KEY);
          window.grecaptcha.execute(
            RECAPTCHA_SITE_KEY, 
            { action: 'submit' }
          )
          .then(token => {
            // Check if component is still mounted before resolving
            if (!isMounted.current) {
              console.error('Component unmounted during reCAPTCHA execution');
              reject(new Error('Component unmounted'));
              return;
            }
            
            if (!token) {
              console.error('Empty token received from reCAPTCHA');
              reject(new Error('Empty reCAPTCHA token'));
              return;
            }
            
            console.log('reCAPTCHA token obtained successfully');
            resolve(token);
          })
          .catch(err => {
            console.error('reCAPTCHA execution error:', err);
            reject(err);
          });
        } catch (err) {
          console.error('Error in grecaptcha.execute:', err);
          reject(err);
        }
      };
      
      // Start the first attempt
      attemptRecaptcha();
    });
  }, [isLocalhost]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if reCAPTCHA is ready before proceeding (skip check on localhost)
    if (!recaptchaReady.current && !isLocalhost) {
      setStatus({
        submitted: false,
        submitting: false,
        info: { 
          error: true, 
          msg: 'reCAPTCHA is not ready yet. Please wait a moment and try again.' 
        }
      });
      return;
    }
    
    setStatus((prevStatus) => ({ ...prevStatus, submitting: true }));

    try {
      console.log('Starting form submission...');
      
      // Get reCAPTCHA token
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha();
        console.log('reCAPTCHA token received');
      } catch (recaptchaError) {
        console.error('reCAPTCHA error:', recaptchaError);
        
        // Only update state if component is still mounted
        if (isMounted.current) {
          setStatus({
            submitted: false,
            submitting: false,
            info: { 
              error: true, 
              msg: `reCAPTCHA verification failed: ${recaptchaError.message || 'Unknown error'}. Please try again later.` 
            }
          });
        }
        return;
      }
      
      if (!recaptchaToken) {
        console.error('No reCAPTCHA token received');
        
        // Only update state if component is still mounted
        if (isMounted.current) {
          setStatus({
            submitted: false,
            submitting: false,
            info: { 
              error: true, 
              msg: 'reCAPTCHA verification failed: No token received. Please try again later.' 
            }
          });
        }
        return;
      }
      
      // Submit form with token
      console.log('Submitting form to API...');
      try {
        // Determine the API URL based on environment
        let apiUrl;
        if (isLocalhost) {
          // Use local development server
          apiUrl = 'http://localhost:5001/api/contact';
        } else {
          // Always use the API URL from config
          apiUrl = `${API_URL}/api/contact`;
          console.log('Using API URL from config:', apiUrl);
        }
          
        console.log('Using API URL:', apiUrl);
          
        // Set a timeout for the API request to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
        
        try {
          const response = await axios.post(apiUrl, {
            ...formData,
            recaptchaToken,
          }, {
            signal: controller.signal,
            timeout: 15000 // 15 second timeout
          });
          
          // Clear the timeout since the request completed
          clearTimeout(timeoutId);

          console.log('API response:', response.data);
          
          if (isMounted.current) {
            setStatus({
              submitted: true,
              submitting: false,
              info: { error: false, msg: response.data.message || 'Your message has been sent successfully!' },
            });

            setFormData({
              name: '',
              email: '',
              message: '',
            });
          }
        } catch (fetchError) {
          // Clear the timeout
          clearTimeout(timeoutId);
          
          if (fetchError.name === 'AbortError' || fetchError.code === 'ECONNABORTED') {
            throw new Error('Request timed out. Please try again later.');
          }
          throw fetchError;
        }
      } catch (apiError) {
        console.error('API request error:', apiError);
        
        let errorMessage = 'An error occurred. Please try again later.';
        
        if (apiError.response) {
          console.error('API error response:', apiError.response.data);
          errorMessage = apiError.response.data.error || errorMessage;
        } else if (apiError.request) {
          // The request was made but no response was received
          console.error('No response received:', apiError.request);
          errorMessage = 'No response from server. Please check your connection and try again.';
        } else {
          errorMessage = apiError.message || errorMessage;
        }
        
        if (isMounted.current) {
          setStatus({
            submitted: false,
            submitting: false,
            info: { error: true, msg: errorMessage },
          });
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      if (isMounted.current) {
        setStatus({
          submitted: false,
          submitting: false,
          info: { error: true, msg: error.message || 'An unexpected error occurred. Please try again later.' },
        });
      }
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6 my-8">
      <h2 className="text-2xl font-heading font-bold text-dark mb-6">Contact Us</h2>
      
      {isLocalhost && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
          <p>Localhost testing mode: reCAPTCHA verification is simulated</p>
        </div>
      )}
      
      {status.info.error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          <p>{status.info.msg}</p>
        </div>
      )}
      
      {status.submitted && !status.info.error && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          <p>{status.info.msg || 'Your message has been sent successfully!'}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        <div className="mb-6">
          <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="4"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          ></textarea>
        </div>
        
        <div className="mb-6 text-xs text-gray-500">
          This site is protected by reCAPTCHA v3 and the Google
          <a href="https://policies.google.com/privacy" className="text-primary hover:underline"> Privacy Policy</a> and
          <a href="https://policies.google.com/terms" className="text-primary hover:underline"> Terms of Service</a> apply.
        </div>
        
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={status.submitting || (!recaptchaReady.current && !isLocalhost)}
            className="bg-primary text-white py-2 px-8 rounded-md hover:bg-primary/80 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-70"
          >
            {status.submitting ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm; 